import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axios, { AxiosProgressEvent } from 'axios';
import { SD_SPNameList } from '../../Utility/StaticDatas';
import { TblFileDocs } from '../../Interfaces/DataBase/Client';
import { getAuthToken, removeAuthToken } from '../../Helper/authToken';

// Base query for upload API
const uploadBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_APIBaseUrl}/uploads`,
});

// Base query for file docs API
const fileDocsBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_APIBaseUrl}/AuthDataGet/ExecuteJson/${SD_SPNameList.DB_SP_TblFileDocs}`,
});

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_APIBaseUrl}/uploads`,
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Define a callback type for progress
type ProgressCallback = (progressEvent: AxiosProgressEvent) => void;

// Function to handle multiple file uploads with progress
const multiUploadFileWithProgress = (
  formData: FormData,
  onProgress: ProgressCallback
) => {
  return axiosInstance.post('multiClientWebPost', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: onProgress,
  });
};

const ClientFileUploadApi = createApi({
  reducerPath: 'ClientFileUploadApi',
  baseQuery: async (args, api, extraOptions) => {
    try {
      const result =
        args.url.includes('uploads') || args.url.includes('ClientFileDelete')
          ? await uploadBaseQuery(args, api, extraOptions)
          : await fileDocsBaseQuery(args, api, extraOptions);
      return result;
    } catch (error: any) {
      if (error.status === 401) {
        removeAuthToken();
        window.location.replace('/auth/login');
      }
      throw error;
    }
  },
  tagTypes: ['ClientFileUpload'],
  endpoints: (builder) => ({
    multiClientUploadFile: builder.mutation({
      queryFn: async (
        {
          formData,
          onUploadProgress,
        }: {
          formData: FormData;
          onUploadProgress: ProgressCallback;
        },
        _queryApi,
        _extraOptions
      ) => {
        try {
          const response = await multiUploadFileWithProgress(
            formData,
            onUploadProgress
          );
          return { data: response.data };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status,
              data: error.response?.data,
            },
          };
        }
      },
      invalidatesTags: ['ClientFileUpload'],
    }),
    deleteClientFile: builder.mutation({
      query: (fileId: number) => ({
        url: `ClientFileDelete/${fileId}`,
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }),
      invalidatesTags: ['ClientFileUpload'],
    }),
    getAllFileDocs: builder.query({
      query: (data: TblFileDocs) => ({
        url: '3',
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          'Content-type': 'application/json',
        },
        body: data,
      }),
      providesTags: ['ClientFileUpload'],
    }),
  }),
});
export const {
  useMultiClientUploadFileMutation,
  useDeleteClientFileMutation,
  useGetAllFileDocsQuery,
} = ClientFileUploadApi;

export default ClientFileUploadApi;
